import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  styled,
} from '@mui/material';
import { Visibility, VisibilityOff, Person, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PartnerSafeApi } from './EnvFiles';
import logogpImage from '../assets/logogp.png';

const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  minWidth: '100vw',
  margin: 0,
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#1E1E1E',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '200vw',
    height: '200vh',
    background: 'radial-gradient(circle at center, #2E2E2E 0%, #1E1E1E 50%)',
    opacity: 0.6,
    animation: 'pulse 8s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'translate(-50%, -50%) scale(1)',
    },
    '50%': {
      transform: 'translate(-50%, -50%) scale(1.2)',
    },
    '100%': {
      transform: 'translate(-50%, -50%) scale(1)',
    },
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(4),
  background: 'rgba(18, 18, 18, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: '20px',
  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  width: '100%',
  maxWidth: '400px',
  overflow: 'hidden',
  animation: 'fadeIn 0.5s ease-out',
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
      transform: 'translateY(-20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const LogoContainer = styled(Box)({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '2rem',
  '& img': {
    width: '120px',
    height: 'auto',
    filter: 'brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(288deg) brightness(102%) contrast(102%)',
    animation: 'logoFloat 6s ease-in-out infinite',
  },
  '@keyframes logoFloat': {
    '0%, 100%': {
      transform: 'translateY(0) rotate(0deg)',
    },
    '25%': {
      transform: 'translateY(-15px) rotate(5deg)',
    },
    '75%': {
      transform: 'translateY(15px) rotate(-5deg)',
    },
  },
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    transition: 'all 0.3s ease',
    background: 'rgba(255, 255, 255, 0.05)',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.08)',
    },
    '&.Mui-focused': {
      background: 'rgba(255, 255, 255, 0.1)',
      transform: 'scale(1.02)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiOutlinedInput-input': {
    color: 'rgba(255, 255, 255, 0.9)',
  },
}));

const StyledButton = styled(Button)({
  marginTop: '16px',
  padding: '12px',
  backgroundColor: '#00FF00',
  color: '#000000',
  fontWeight: 600,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#00CC00',
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(0, 255, 0, 0.3)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&.Mui-disabled': {
    backgroundColor: 'rgba(0, 255, 0, 0.5)',
  },
});

const Login = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nomeDeUsuario: '',
    senha: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${PartnerSafeApi}/api/v1/Contas/Login`, formData);
      await onLoginSuccess(response?.data?.email || formData.email, response.data.token);
      setSnackbar({
        open: true,
        message: 'Login realizado com sucesso!',
        severity: 'success',
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Erro no login:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao fazer login. Verifique suas credenciais.',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <StyledContainer>
      <StyledPaper elevation={3}>
        <LogoContainer>
          <img 
            src={logogpImage} 
            alt="Logo GP" 
            style={{ 
              filter: 'brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(288deg) brightness(102%) contrast(102%)'
            }} 
          />
        </LogoContainer>

        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 600,
            textAlign: 'center',
            color: '#00FF00',
          }}
        >
          PartnerSafe
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <StyledTextField
            fullWidth
            label="Usuário"
            name="nomeDeUsuario"
            value={formData.nomeDeUsuario}
            onChange={handleChange}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            fullWidth
            label="Senha"
            name="senha"
            type={showPassword ? 'text' : 'password'}
            value={formData.senha}
            onChange={handleChange}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 3, mb: 2 }}>
            <StyledButton
              type="submit"
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Entrar'}
            </StyledButton>

            <StyledButton
              fullWidth
              type="button"
              onClick={() => navigate('/register')}
              sx={{
                backgroundColor: '#FFFFFF',
                color: '#000000',
                '&:hover': {
                  backgroundColor: '#DDDDDD',
                  boxShadow: '0 5px 15px rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              Registrar-se
            </StyledButton>
          </Box>
        </Box>
      </StyledPaper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </StyledContainer>
  );
};

export default Login;